import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";
import {
  Chart, LineController, LineElement, PointElement,
  LinearScale, CategoryScale, Tooltip, Filler,
} from "chart.js";

// Filler is REQUIRED in Chart.js 4 for `fill: true` to render the area under the
// Sales line. Legend is NOT registered — the chart has its own .evo-legend chips
// below the canvas (matching the style-guide design).
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler);

// Connects to data-controller="overview-sales-chart"
//
// The Event Overview "Sales & Refunds" chart (design .evo-chart-card). Two-series
// Chart.js line/area: GROSS sales (blue, filled) vs refunds (red, dashed). The
// full event-lifetime series is server-rendered once into a JSON <script>; the
// range selector filters it client-side (lifetime = all; 7d/30d = last N days;
// custom = a flatpickr-selected [start,end] sub-window) so there's no extra endpoint.
//
// The canvas lives inside a height-clamped .evo-chart-wrap (320px desktop /
// 230px mobile). maintainAspectRatio:false PAIRS with that clamp — without the
// fixed-height wrapper a responsive canvas grows without bound (the runaway-
// growth bug). Do not remove either half.
export default class extends Controller {
  static targets = [
    "canvas", "range", "data", "srBody",
    "custom", "customInput", "customClear", "caption",
  ];

  connect() {
    this.full = this.parseData();
    this.chart = null;
    this.customRange = null; // [startISO, endISO] once a custom window is applied
    this.initCustomPicker();
    if (this.hasCanvasTarget && this.full) this.render("lifetime");
  }

  disconnect() {
    if (this.chart) { this.chart.destroy(); this.chart = null; }
    if (this.flatpickr) { this.flatpickr.destroy(); this.flatpickr = null; }
  }

  parseData() {
    if (!this.hasDataTarget) return null;
    try {
      return JSON.parse(this.dataTarget.textContent);
    } catch (e) {
      console.error("overview-sales-chart: bad series payload", e);
      return null;
    }
  }

  changeRange(event) {
    const range = event.target.value;
    const isCustom = range === "custom";
    // Reveal the picker only for the custom option; presets hide it + clear caption.
    if (this.hasCustomTarget) this.customTarget.classList.toggle("d-none", !isCustom);
    if (isCustom) {
      // Re-render at whatever custom window is already chosen (or lifetime until
      // one is picked), then open the picker so the next click is the date choice.
      this.render("custom");
      if (this.flatpickr) this.flatpickr.open();
    } else {
      this.customRange = null;
      this.hideCaption();
      this.render(range);
    }
  }

  // --- custom range (flatpickr, mode: "range") -----------------------------
  //
  // Reuses the app's established date-picker (flatpickr range mode — same pattern
  // as date_range_filter_controller and the Reports custom range). Clamped to the
  // event's data window via min/maxDate so a selection can't fall outside the
  // lifetime series.
  initCustomPicker() {
    if (!this.hasCustomInputTarget || !this.full || !this.full.dates || !this.full.dates.length) return;
    const dates = this.full.dates;
    this.flatpickr = flatpickr(this.customInputTarget, {
      mode: "range",
      dateFormat: "Y-m-d",
      minDate: dates[0],
      maxDate: dates[dates.length - 1],
      onClose: (selected) => {
        // Empty / cancelled selection → revert to the prior window.
        if (selected.length < 2) return;
        // flatpickr already returns ascending order, but guard start>end anyway
        // by sorting (graceful swap rather than ignoring).
        const sorted = [...selected].sort((a, b) => a - b);
        this.customRange = [this.toISO(sorted[0]), this.toISO(sorted[1])];
        this.render("custom");
      },
    });
  }

  applyCustom() {
    if (this.flatpickr) this.flatpickr.open();
  }

  clearCustom() {
    this.customRange = null;
    if (this.flatpickr) this.flatpickr.clear();
    this.hideCaption();
    this.render("custom"); // custom with no window === full lifetime
  }

  // Slice the lifetime series down to the chosen range. lifetime keeps the whole
  // series; 7d/30d keep the last N points; custom keeps [start,end] (inclusive)
  // mapped via the parallel ISO dates, or the whole series if none chosen yet.
  windowFor(range) {
    const { labels, sales, refunds, dates } = this.full;
    if (range === "custom") {
      if (!this.customRange || !dates) return this.fullWindow();
      const [startISO, endISO] = this.customRange;
      let lo = dates.findIndex((d) => d >= startISO);
      let hi = -1;
      for (let i = dates.length - 1; i >= 0; i--) {
        if (dates[i] <= endISO) { hi = i; break; }
      }
      if (lo === -1 || hi === -1 || lo > hi) return this.fullWindow();
      this.showCaption(labels[lo], labels[hi]);
      return {
        labels: labels.slice(lo, hi + 1),
        sales: sales.slice(lo, hi + 1),
        refunds: refunds.slice(lo, hi + 1),
      };
    }
    let n = labels.length;
    if (range === "7d") n = Math.min(7, labels.length);
    else if (range === "30d") n = Math.min(30, labels.length);
    const start = labels.length - n;
    return {
      labels: labels.slice(start),
      sales: sales.slice(start),
      refunds: refunds.slice(start),
    };
  }

  fullWindow() {
    const { labels, sales, refunds } = this.full;
    return { labels: [...labels], sales: [...sales], refunds: [...refunds] };
  }

  toISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  showCaption(startLabel, endLabel) {
    if (!this.hasCaptionTarget) return;
    const tmpl = this.captionTarget.dataset.template || "%{start} – %{end}";
    this.captionTarget.textContent = tmpl
      .replace("%{start}", startLabel)
      .replace("%{end}", endLabel);
    this.captionTarget.classList.remove("d-none");
  }

  hideCaption() {
    if (!this.hasCaptionTarget) return;
    this.captionTarget.textContent = "";
    this.captionTarget.classList.add("d-none");
  }

  render(range) {
    const data = this.windowFor(range);
    // Show point markers for short/sparse windows (≤12 days) so a one- or
    // few-day series reads honestly instead of an invisible flat line; hidden
    // when dense. Mirrors the Reports trend chart's pointRadius rule.
    const pointRadius = data.labels.length <= 12 ? 3 : 0;
    if (this.chart) this.chart.destroy();
    const ctx = this.canvasTarget.getContext("2d");
    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: this.full.salesLabel,
            data: data.sales,
            borderColor: "#2684FF",
            backgroundColor: "rgba(38,132,255,.10)",
            borderWidth: 2,
            fill: true,
            tension: 0.35,
            pointRadius,
            pointHoverRadius: 4,
          },
          {
            label: this.full.refundsLabel,
            data: data.refunds,
            borderColor: "#FF6B6B",
            backgroundColor: "rgba(255,107,107,.06)",
            borderWidth: 2,
            borderDash: [5, 4],
            fill: false,
            tension: 0.35,
            pointRadius,
            pointHoverRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false, // REQUIRED — pairs with the .evo-chart-wrap height clamp
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false }, // own .evo-legend chips below the canvas
          tooltip: {
            backgroundColor: "rgba(255,255,255,.96)",
            titleColor: "#101828", bodyColor: "#475467",
            borderColor: "#e4e7ec", borderWidth: 1, padding: 10,
            callbacks: {
              label: (c) => `${c.dataset.label}: ${this.formatMoney(c.parsed.y)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: "rgba(16,24,40,.06)" },
            ticks: { color: "#525252", callback: (v) => this.formatMoney(v) },
          },
          x: { grid: { display: false }, ticks: { color: "#525252", maxTicksLimit: 8 } },
        },
      },
    });
  }

  formatMoney(v) {
    return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
}
